import {
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from 'components'
import { Loader2 } from 'lucide-react'
import type { TikTokCreatorInfo } from '../hooks/useTikTokCreatorInfo'
import type { TikTokPrivacyLevel } from '../types'
import {
  PRIVACY_LABELS,
  TIKTOK_BRANDED_CONTENT_POLICY_URL,
  TIKTOK_MUSIC_USAGE_URL,
  tiktokPostSettingsProblem,
  type TikTokPostSettingsDraft,
} from '../utils/tiktok-post-settings'

/** The label TikTok will put on the video for the chosen commercial disclosure. */
function commercialLabel(draft: TikTokPostSettingsDraft): string | null {
  if (!draft.commercialContent) return null
  if (draft.brandedContent) return "Your video will be labeled 'Paid partnership'."
  if (draft.brandOrganic) return "Your video will be labeled 'Promotional content'."
  return null
}

interface TikTokPostSettingsFieldsProps {
  draft: TikTokPostSettingsDraft
  onChange: (next: TikTokPostSettingsDraft) => void
  creatorInfo: TikTokCreatorInfo | null
  creatorInfoLoading: boolean
  /** Why creator info could not load (e.g. TikTok not connected). */
  creatorInfoError: string | null
  /** True for Taka-made video (labelled AI-generated on TikTok); false for a customer upload. */
  aiGenerated: boolean
  /** Hide the consent + processing notes (the editor shows the choices only). */
  compact?: boolean
}

const ROW = 'flex items-center justify-between gap-3'
const LABEL = 'text-paper text-[13px] font-medium'
const HINT = 'text-paper-faint text-[12px]'

/**
 * The per-post choices TikTok's Content Sharing Guidelines require before a
 * Direct Post. Shared by the composer's "Post to TikTok" dialog and the TikTok
 * editor (so scheduled / autopilot posts carry them too):
 *   - audience: a dropdown of the creator's own `privacy_level_options`, no default
 *   - comments / duet / stitch: off by default, greyed out when the creator disabled them
 *   - commercial content disclosure: opt-in toggle with "Your brand" / "Branded content"
 *   - the AI-generated label note, the consent line and the processing notice
 */
export function TikTokPostSettingsFields({
  draft,
  onChange,
  creatorInfo,
  creatorInfoLoading,
  creatorInfoError,
  aiGenerated,
  compact = false,
}: TikTokPostSettingsFieldsProps) {
  const set = (patch: Partial<TikTokPostSettingsDraft>) => onChange({ ...draft, ...patch })
  const options = creatorInfo?.privacyLevelOptions ?? []
  const problem = tiktokPostSettingsProblem(draft)
  const label = commercialLabel(draft)

  return (
    <div className="space-y-4">
      {/* Audience */}
      <div className="space-y-1.5">
        <div className={LABEL}>Who can view this video</div>
        {creatorInfoLoading ? (
          <div className={`${HINT} flex items-center gap-2`}>
            <Loader2 size={12} className="animate-spin" />
            Loading your TikTok settings...
          </div>
        ) : creatorInfoError ? (
          <p className="text-[12px] text-[color:var(--alert-error-text)]">{creatorInfoError}</p>
        ) : (
          <Select
            value={draft.privacyLevel ?? ''}
            onValueChange={v => set({ privacyLevel: v as TikTokPrivacyLevel })}
          >
            <SelectTrigger variant="dark" className="w-full" aria-label="Who can view this video">
              <SelectValue placeholder="Select who can view" />
            </SelectTrigger>
            <SelectContent variant="dark">
              {options.map(level => (
                <SelectItem key={level} value={level}>
                  {PRIVACY_LABELS[level as TikTokPrivacyLevel] ?? level}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Interactions */}
      <div className="space-y-2">
        <div className={LABEL}>Allow users to</div>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {(
            [
              ['allowComment', 'Comment', creatorInfo?.commentDisabled],
              ['allowDuet', 'Duet', creatorInfo?.duetDisabled],
              ['allowStitch', 'Stitch', creatorInfo?.stitchDisabled],
            ] as const
          ).map(([key, text, disabledByCreator]) => {
            const disabled = !!disabledByCreator
            const id = `tiktok-${key}`
            // Checkbox and label are siblings: a Radix checkbox is a <button>,
            // and a <button> nested inside a <label> toggles twice per click.
            return (
              <div key={key} className="flex items-center gap-2">
                <Checkbox
                  id={id}
                  variant="dark"
                  checked={!disabled && draft[key]}
                  disabled={disabled}
                  onCheckedChange={checked => set({ [key]: checked === true })}
                />
                <label
                  htmlFor={id}
                  className={`text-[13px] ${disabled ? 'cursor-not-allowed text-paper-faint' : 'cursor-pointer text-paper'}`}
                  title={disabled ? `${text} is turned off in your TikTok settings` : undefined}
                >
                  {text}
                </label>
              </div>
            )
          })}
        </div>
      </div>

      {/* Commercial content disclosure */}
      <div className="space-y-2">
        <div className={ROW}>
          <div>
            <div className={LABEL}>Disclose commercial content</div>
            <div className={HINT}>
              Turn on if this video promotes yourself, a brand, product or service.
            </div>
          </div>
          <Switch
            variant="dark"
            checked={draft.commercialContent}
            onCheckedChange={checked =>
              set({
                commercialContent: checked,
                ...(checked ? {} : { brandOrganic: false, brandedContent: false }),
              })
            }
            aria-label="Disclose commercial content"
          />
        </div>
        {draft.commercialContent && (
          <div className="space-y-2 rounded-lg border border-line-bright bg-bg-3 p-3">
            <div className="flex items-start gap-2">
              <Checkbox
                id="tiktok-brand-organic"
                variant="dark"
                className="mt-0.5"
                checked={draft.brandOrganic}
                onCheckedChange={checked => set({ brandOrganic: checked === true })}
              />
              <label
                htmlFor="tiktok-brand-organic"
                className="cursor-pointer text-[13px] text-paper"
              >
                <span className="font-medium">Your brand</span>
                <span className={`${HINT} block`}>
                  You are promoting yourself or your own business.
                </span>
              </label>
            </div>
            <div className="flex items-start gap-2">
              <Checkbox
                id="tiktok-branded-content"
                variant="dark"
                className="mt-0.5"
                checked={draft.brandedContent}
                onCheckedChange={checked => set({ brandedContent: checked === true })}
              />
              <label
                htmlFor="tiktok-branded-content"
                className="cursor-pointer text-[13px] text-paper"
              >
                <span className="font-medium">Branded content</span>
                <span className={`${HINT} block`}>
                  You are promoting another brand or a third party (paid partnership).
                </span>
              </label>
            </div>
            {label && <p className={HINT}>{label}</p>}
          </div>
        )}
      </div>

      {problem && draft.privacyLevel && (
        <p className="text-[12px] text-[color:var(--alert-error-text)]">{problem}</p>
      )}

      {aiGenerated && (
        <p className={HINT}>
          This video was made with AI, so TikTok will label it as AI-generated.
        </p>
      )}

      {!compact && (
        <div className="space-y-1">
          <p className={HINT}>
            By posting, you agree to TikTok&apos;s{' '}
            {draft.commercialContent && draft.brandedContent && (
              <>
                <a
                  href={TIKTOK_BRANDED_CONTENT_POLICY_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="text-paper underline"
                >
                  Branded Content Policy
                </a>{' '}
                and{' '}
              </>
            )}
            <a
              href={TIKTOK_MUSIC_USAGE_URL}
              target="_blank"
              rel="noreferrer"
              className="text-paper underline"
            >
              Music Usage Confirmation
            </a>
            .
          </p>
          <p className={HINT}>
            After you post, TikTok may take a few minutes to process the video before it shows on
            your profile.
          </p>
        </div>
      )}
    </div>
  )
}
